import { Component, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { filter, firstValueFrom } from 'rxjs';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { TypeAccountingSystemEnums } from 'src/app/enum/accouting-system-enums';
import { TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { AccountingInterfaceDetailTableModel } from 'src/app/models/tables/invoicing/accounting-interface-detail-table.model';
import { AccountingInterfaceTableModel } from 'src/app/models/tables/invoicing/accounting-interface-table.model';
import { UserAuthenticatedService } from 'src/app/services/global-services/user-authenticated.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { AccountingInterfaceService } from './../../../../services/invoicing/accounting-interface.service';

@Component({
  selector: 'app-accounting-interface',
  templateUrl: './accounting-interface.component.html',
  styleUrls: ['./accounting-interface.component.scss'],
  standalone: false
})
export class AccountingInterfaceComponent implements OnInit {
  accountingInterfaceId: number = 0;
  activeTabIndex = 0;
  ascendant: boolean = true;
  columnSort: string | null = null;
  detailData: PagedResponseModel<AccountingInterfaceDetailTableModel> = new PagedResponseModel<AccountingInterfaceDetailTableModel>();
  detailHeader: ColumnResponseModel[] = [];
  filters: ColumnResponseModel[] = [];
  generalHeader: ColumnResponseModel[] = [];
  generalData: PagedResponseModel<AccountingInterfaceTableModel> = new PagedResponseModel<AccountingInterfaceTableModel>();
  limit: number = 10;
  nameWindow: string = 'Interfaz';
  projectName = ProjectName.projectName;
  page: number = 1;
  previousSort: { field: string; isAscendant: number };
  tableActions: TableAction[] = [
    {
      label: 'Editar',
      icon: 'pi pi-pencil',
      action: 'edit',
    },
  ];
  visibleExportCxc: boolean = false;
  visibleFilters: boolean = true;

  constructor(private accountingInterfaceService: AccountingInterfaceService,
    private headerService: HeaderService,
    private router: Router,
    private title: Title,
    private activatedRoute: ActivatedRoute,
    private userAuthenticatedService: UserAuthenticatedService) {
    this.accountingInterfaceId = this.activatedRoute.snapshot.paramMap.get('id') as any;
  }

  async ngOnInit() {
    const filters = localStorage.getItem('accounting-interface');
    filters ? (this.filters = JSON.parse(filters)) : [];
    await Promise.all([
      this.getColumnsForGeneralConsultation(),
      this.getAccountingInterfaceForGeneralConsultation(),
    ]);
    await this.getCompanyById();
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.headerService.setTitle(this.nameWindow);
  }

  async exportDetailConsultation() {
    try {
      const blob = await firstValueFrom(
        this.accountingInterfaceService.exportDetailConsultation(
          this.filters,
          this.columnSort,
          this.ascendant
        )
      );
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Interfaz Detalle.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error(
        'Error al exportar la interfaz detalle.',
        error
      );
    }
  }

  async exportGeneralConsultation() {
    try {
      const blob = await firstValueFrom(
        this.accountingInterfaceService.exportGeneralConsultation(
          this.filters,
          this.columnSort,
          this.ascendant
        )
      );
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Interfaz.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error al exportar la interfaz.', error);
    }
  }

  async getColumnsForDetailConsultation() {
    try {
      if (this.detailHeader.length === 0) {
        this.detailHeader = await firstValueFrom(
          this.accountingInterfaceService.getColumnsForDetailConsultation()
        );
      }
    } catch (error) {
      console.error(
        'Error al buscar las columnas para la consulta detalle.',
        error
      );
    }
  }

  async getColumnsForGeneralConsultation() {
    try {
      if (this.generalHeader.length === 0) {
        this.generalHeader = await firstValueFrom(
          this.accountingInterfaceService.getColumnsForGeneralConsultation()
        );
      }
    } catch (error) {
      console.error(
        'Error al buscar las columnas para la consulta general.',
        error
      );
    }
  }

  async getCompanyById() {
    const company = (await firstValueFrom(
      this.userAuthenticatedService.companyObservable.pipe(
        filter((company) => company && company.companyId !== 0)
      )
    ));

    let typeInterface = '';
    if (company.typeAccountingSystemId == TypeAccountingSystemEnums.cxc) {
      this.visibleExportCxc = true;
      typeInterface = 'CxC';
    } else if (company.typeAccountingSystemId == TypeAccountingSystemEnums.contab) {
      this.visibleExportCxc = false;
      typeInterface = 'Contable';
    } else {
      this.visibleExportCxc = true;
    }

    this.nameWindow = `${this.nameWindow} ${typeInterface}`
  }

  async getAccountingInterfaceForGeneralConsultation() {
    try {
      this.generalData = await firstValueFrom(
        this.accountingInterfaceService.getAccountInterfacesForGeneralConsultation(
          this.page,
          this.limit,
          this.columnSort,
          this.ascendant,
          this.filters
        )
      );
    } catch (error) {
      console.error('Error al buscar las interfaces.', error);
    }
  }

  async getAccountingInterfaceDetailsForDetailConsultation() {
    try {
      this.detailData = await firstValueFrom(
        this.accountingInterfaceService.getAccountingInterfaceDetailsForDetailConsultation(
          this.page,
          this.limit,
          this.columnSort,
          this.ascendant,
          this.filters
        ));
    } catch (error) {
      console.error('Error al buscar la consulta detalle.', error);
    }
  }

  public async handleTabChange(tabIndex: number) {
    this.generalHeader.length =
      this.generalHeader.length === 1 ? 0 : this.generalHeader.length;
    this.detailHeader.length =
      this.detailHeader.length === 1 ? 0 : this.detailHeader.length;

    if (this.activeTabIndex === tabIndex) return;

    this.page = 1;
    this.limit = 10;
    this.activeTabIndex = tabIndex;

    switch (this.activeTabIndex) {
      case 0:
        await Promise.all([
          this.getColumnsForGeneralConsultation(),
          this.getAccountingInterfaceForGeneralConsultation(),
        ]);
        this.generalData = {
          ...this.generalData,
          currentPage: 1,
          pageSize: 10,
        };
        break;
      case 1:
        await Promise.all([
          this.getColumnsForDetailConsultation(),
          this.getAccountingInterfaceDetailsForDetailConsultation(),
        ]);
        this.detailData = {
          ...this.detailData,
          currentPage: 1,
          pageSize: 10,
        };
        break;
    }
  }

  newAccountingInterface() {
    this.router.navigate(['/accounting-interface/register']);
  }

  onAction(event: {
    action: string;
    item?: AccountingInterfaceTableModel;
    selectedItems?: number[];
  }) {
    if (event.action === 'edit' && event.item) {
      this.router.navigate([
        `/accounting-interface/register/${event.item.accountingInterfaceId}`,
      ]);
    }
  }

  async onPageChange(event: {
    page: number;
    pageSize: number;
    sortField: string | undefined;
    sortOrder: number;
  }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    switch (this.activeTabIndex) {
      case 0:
        await this.getAccountingInterfaceForGeneralConsultation();
        break;
      case 1:
        await this.getAccountingInterfaceDetailsForDetailConsultation();
        break;
    }
  }

  async onExport() {
    switch (this.activeTabIndex) {
      case 0:
        await this.exportGeneralConsultation();
        break;
      case 1:
        await this.exportDetailConsultation();
        break;
    }
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters
      .map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index)
      .map((item: any) => item.value);

    this.filters = this.removeDuplicates(filters);

    // Procesar cada filtro
    this.filters.forEach((filter: any) => this.processFilter(filter));
  }

  private removeDuplicates(filters: any[]): any[] {
    return filters.filter(
      (value, index, self) =>
        index ===
        self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

  private processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (
      valueFilter === null ||
      valueFilter === undefined ||
      valueFilter === '' ||
      valueFilter === 0
    ) {
      this.filters = this.filters.filter(
        (f) => f.propertyName !== propertyName
      );
    } else {
      const existingFilter = this.filters.find(
        (f) => f.propertyName === propertyName
      );
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }
}
